import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { MATERIALS } from "@/lib/content";
import { goToEstimate } from "@/lib/scroll";
import { Eyebrow, Reveal } from "./Reveal";

const Card = ({ m, i, onOpen }) => (
  <Reveal delay={i * 0.08}>
    <button
      onClick={() => onOpen(m)}
      data-testid={`material-card-${m.id}`}
      className="group relative block aspect-[3/4] w-full overflow-hidden rounded-[2px] bg-[#1a1f29] text-left"
    >
      <img src={m.img} alt={`${m.name} countertop surface`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-110" />
      <span className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/30 to-transparent" />
      <span className="absolute left-5 top-5 font-display text-sm text-[#ECE6DA]/80">0{i + 1}</span>
      <span className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white transition-[background-color,transform] duration-500 group-hover:rotate-45 group-hover:bg-[#CDA85B] group-hover:text-[#0B0F17]">
        <ArrowUpRight className="h-4 w-4" />
      </span>
      <span className="absolute inset-x-5 bottom-5">
        <span className="block text-[11px] uppercase tracking-[0.2em] text-[#CDA85B]">{m.tag}</span>
        <span className="mt-2 block font-display text-4xl text-[#F5F0E6]">{m.name}</span>
        <span className="mt-2 block max-h-0 overflow-hidden text-sm text-[#D6D3CC] opacity-0 transition-[max-height,opacity] duration-700 group-hover:max-h-24 group-hover:opacity-100 max-lg:max-h-24 max-lg:opacity-100">{m.summary}</span>
      </span>
    </button>
  </Reveal>
);

export const Materials = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="countertops" data-testid="materials-section" className="bg-[#F5F0E6] py-24 text-[#0B0F17] lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow light>Countertops</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Four stones. <span className="italic text-[#8a6d2f]">One standard</span> of craft.</h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="text-[#3E434C]">Not sure which surface fits your home? Tap a material to compare durability, care and where it shines, then request an estimate in that stone.</p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MATERIALS.map((m, i) => <Card key={m.id} m={m} i={i} onOpen={setActive} />)}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="material-dialog" className="max-w-3xl overflow-hidden border-white/10 bg-[#0E131D] p-0 text-[#F5F0E6]">
          {active && (
            <div className="grid sm:grid-cols-2">
              <img src={active.img} alt={`${active.name} surface`} className="h-56 w-full object-cover sm:h-full" />
              <div className="p-7">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#CDA85B]">{active.tag}</p>
                <DialogTitle className="mt-2 font-display text-4xl font-normal">{active.name}</DialogTitle>
                <DialogDescription className="mt-3 text-sm leading-relaxed text-[#C4C1B9]">{active.detail}</DialogDescription>
                <dl className="mt-6 grid grid-cols-3 gap-3 border-y border-white/10 py-4">
                  {active.traits.map(([k, v]) => (
                    <div key={k}><dt className="text-[11px] uppercase tracking-[0.16em] text-[#AEB3BC]">{k}</dt><dd className="mt-1 text-sm">{v}</dd></div>
                  ))}
                </dl>
                <p className="mt-4 text-sm text-[#C4C1B9]"><span className="text-[#CDA85B]">Best for:</span> {active.bestFor}</p>
                <button
                  data-testid="material-dialog-estimate-btn"
                  onClick={() => { const name = active.name; setActive(null); setTimeout(() => goToEstimate({ material: name }), 250); }}
                  className="ks-btn-gold mt-6 w-full justify-center"
                >
                  Get an estimate in {active.name}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
